/**
 * /van-ban/:id - corpus document detail (M2 v2 Stage A, P-A5).
 *
 * Spec: specs/M2-EXECUTION-PLAN-v2.md §4 P-A5 AC-6 -> AC-15.
 * F5 audit (2026-05-21): EXTEND existing POC, rewire v2 schema +
 * replace noi-dung tab stub voi RawHtmlViewer/TocSidebar/FallbackBanner.
 *
 * Phase 2 redesign (2026-05-22) Track B: Direction A "Tu phap restraint".
 *   - Drop orange tab underline -> navy
 *   - Hieu Luc HHL banner: red token (KHONG hardcoded #dc2626)
 *   - Timeline marker normal = navy diamond, danger = red diamond
 *   - H1 + section H2 -> Source Serif 4 (font-display) navy
 *   - Tab active underline 2px navy
 *
 * Tab routing via React Router params:
 *   /van-ban/:id            -> Tổng quan (default)
 *   /van-ban/:id/noi-dung   -> Nội dung w/ RawHtmlViewer + TocSidebar
 *   /van-ban/:id/pdf        -> Văn bản gốc (vbpl.vn outbound link)
 *   /van-ban/:id/hieu-luc   -> Hiệu lực timeline
 *   /van-ban/:id/luoc-do    -> Lược đồ reference graph
 *   /van-ban/:id/tai-ve     -> Tải về external link
 *
 * Disclaimer block 100% tab footer (legal-upl HARD VETO trigger if missing).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  fetchDocDetail,
  fetchReferences,
  SCOPE_LABEL_VI,
  type DocDetail,
  type ReferenceEdge,
} from '../lib/corpus-api';
import { EffStatusBadge } from '../components/corpus/EffStatusBadge';
import { RefTypeBadge } from '../components/corpus/RefTypeBadge';
import { CorpusDisclaimerBlock } from '../components/corpus/CorpusDisclaimerBlock';
import { FallbackBanner } from '../components/corpus/FallbackBanner';
import { TocSidebar, type TocEntry } from '../components/corpus/TocSidebar';
import { RawHtmlViewer, type RawHtmlViewerHandle } from '../components/corpus/RawHtmlViewer';
import { PdfViewer, PdfRefsPlaceholder } from '../components/corpus/PdfViewer';
import { applyCorpusDocSeo } from '../lib/seo-corpus-meta';
import { trackEvent } from '../lib/analytics';

type TabKey = 'tong-quan' | 'noi-dung' | 'pdf' | 'hieu-luc' | 'luoc-do' | 'tai-ve';

const TABS: Array<{ key: TabKey; label: string; slug: string }> = [
  { key: 'tong-quan', label: 'Tổng quan', slug: '' },
  { key: 'noi-dung', label: 'Nội dung', slug: 'noi-dung' },
  { key: 'pdf', label: 'Văn bản gốc', slug: 'pdf' },
  { key: 'hieu-luc', label: 'Hiệu lực', slug: 'hieu-luc' },
  { key: 'luoc-do', label: 'Lược đồ', slug: 'luoc-do' },
  { key: 'tai-ve', label: 'Tải về', slug: 'tai-ve' },
];

function tabFromPath(rest: string | undefined): TabKey {
  switch (rest) {
    case 'noi-dung':
      return 'noi-dung';
    case 'pdf':
      return 'pdf';
    case 'hieu-luc':
      return 'hieu-luc';
    case 'luoc-do':
      return 'luoc-do';
    case 'tai-ve':
      return 'tai-ve';
    default:
      return 'tong-quan';
  }
}

export function CorpusDetailRoute() {
  const { id, tab: tabParam } = useParams();
  const navigate = useNavigate();
  const docId = id ?? '';
  const activeTab = tabFromPath(tabParam);

  const [doc, setDoc] = useState<DocDetail | null>(null);
  const [error, setError] = useState<'not_found' | 'other' | null>(null);

  useEffect(() => {
    if (!docId) {
      setError('not_found');
      return;
    }
    let cancelled = false;
    setDoc(null);
    setError(null);
    fetchDocDetail(docId)
      .then((data) => {
        if (!cancelled) setDoc(data);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message === 'not_found' ? 'not_found' : 'other');
      });
    return () => {
      cancelled = true;
    };
  }, [docId]);

  // GA4 key event: a legal document was opened (once per doc). PDPA-gated.
  useEffect(() => {
    if (doc?.doc_id) trackEvent('doc_opened', { doc_id: doc.doc_id });
  }, [doc?.doc_id]);

  useEffect(() => {
    if (!doc) return;
    applyCorpusDocSeo({
      doc_id: doc.doc_id,
      title: doc.title,
      doc_number: doc.doc_num,
      doc_type_name: doc.doc_type_name ?? doc.doc_type_code ?? null,
      agency: doc.agency_name,
      issue_date: doc.issue_date,
      eff_from: doc.eff_from,
      eff_to: doc.eff_to,
      eff_status_code: doc.eff_status_code,
      summary: doc.summary_text,
    });
  }, [doc]);

  const switchTab = useCallback(
    (key: TabKey) => {
      const def = TABS.find((t) => t.key === key);
      const slug = def?.slug ?? '';
      navigate(
        slug
          ? `/van-ban/${encodeURIComponent(docId)}/${slug}`
          : `/van-ban/${encodeURIComponent(docId)}`,
      );
    },
    [docId, navigate],
  );

  if (error === 'not_found') {
    return (
      <main style={{ maxWidth: 720, margin: '64px auto', padding: '0 24px', textAlign: 'center' }}>
        <h1
          style={{
            fontSize: 'clamp(24px, 3vw, 32px)',
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            color: 'var(--asklaw-navy)',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          Không tìm thấy văn bản
        </h1>
        <p style={{ marginTop: 12, color: 'var(--asklaw-ink-soft)', lineHeight: 1.55 }}>
          Văn bản này có thể đã bị gỡ hoặc nằm ngoài phạm vi dữ liệu hiện tại của Asklaw.
        </p>
        <p style={{ marginTop: 24 }}>
          <Link
            to="/tim-kiem"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '10px 20px',
              background: 'var(--asklaw-navy)',
              color: 'var(--asklaw-paper)',
              borderRadius: 8,
              textDecoration: 'none',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 14,
              minHeight: 44,
              border: '1px solid var(--asklaw-navy-deep)',
            }}
          >
            Quay lại tìm kiếm
          </Link>
        </p>
      </main>
    );
  }

  if (error === 'other' || !doc) {
    return (
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 16px' }}>
        {error === 'other' ? (
          <div
            role="alert"
            style={{
              padding: 20,
              border: '1px solid var(--asklaw-red-soft)',
              background: 'var(--asklaw-red-soft)',
              borderLeft: '4px solid var(--asklaw-red)',
              borderRadius: 12,
              color: 'var(--asklaw-red-deep)',
            }}
          >
            Đã có lỗi xảy ra khi tải văn bản. Vui lòng thử lại sau ít phút.
          </div>
        ) : (
          <DocSkeleton />
        )}
      </main>
    );
  }

  return (
    <main
      style={{
        maxWidth: 1180,
        margin: '0 auto',
        padding: '24px 16px 56px',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <a
        href="#tab-content"
        className="skip-to-content"
        style={{
          position: 'absolute',
          left: -9999,
          top: 'auto',
          width: 1,
          height: 1,
          overflow: 'hidden',
        }}
      >
        Đi tới nội dung chính
      </a>
      <Breadcrumb title={doc.title} />

      <header style={{ marginTop: 12, marginBottom: 20 }}>
        <h1
          style={{
            margin: '0 0 14px',
            fontSize: 'clamp(22px, 3vw, 32px)',
            fontWeight: 600,
            lineHeight: 1.3,
            color: 'var(--asklaw-navy)',
            fontFamily: 'var(--font-display)',
            letterSpacing: '-0.01em',
            maxWidth: 760,
          }}
        >
          {doc.title}
        </h1>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            color: 'var(--asklaw-muted)',
            fontSize: 13.5,
          }}
        >
          <EffStatusBadge status={doc.eff_status_code} size="md" />
          {doc.doc_num ? (
            <>
              <span aria-hidden style={{ color: 'var(--asklaw-border-strong)' }}>|</span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13.5,
                  color: 'var(--asklaw-navy-deep)',
                  fontWeight: 500,
                }}
              >
                {doc.doc_num}
              </span>
            </>
          ) : null}
          {doc.agency_name ? (
            <>
              <span aria-hidden style={{ color: 'var(--asklaw-border-strong)' }}>|</span>
              <span style={{ fontSize: 13.5 }}>{doc.agency_name}</span>
            </>
          ) : null}
          {doc.issue_date ? (
            <>
              <span aria-hidden style={{ color: 'var(--asklaw-border-strong)' }}>|</span>
              <span style={{ fontSize: 13.5 }}>{formatDateVi(doc.issue_date)}</span>
            </>
          ) : null}
        </div>
      </header>

      <TabNav active={activeTab} onChange={switchTab} />

      {/*
        Content-width discipline: tab card/table (Tong quan, Hieu luc, Luoc do,
        Van ban goc, Tai ve) dung 1 reading-width chung (CONTENT_MAX_W) de moi
        tab co cung mep trai+phai -> dong bo, tranh table Luoc do dac full 1180px
        nhin lech (founder bug 2026-06-11). Tab Noi dung KHONG gioi han: toan van
        luat + ToC sidebar can full main width de doc.
      */}
      <div
        id="tab-content"
        style={{
          marginTop: 24,
          maxWidth: activeTab === 'noi-dung' ? undefined : CONTENT_MAX_W,
        }}
      >
        {activeTab === 'tong-quan' ? <TabTongQuan doc={doc} /> : null}
        {activeTab === 'noi-dung' ? <TabNoiDung doc={doc} /> : null}
        {activeTab === 'pdf' ? <TabPdf doc={doc} /> : null}
        {activeTab === 'hieu-luc' ? <TabHieuLuc doc={doc} /> : null}
        {activeTab === 'luoc-do' ? <TabLuocDo doc={doc} /> : null}
        {activeTab === 'tai-ve' ? <TabTaiVe doc={doc} /> : null}
      </div>

      <CorpusDisclaimerBlock />
    </main>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb + TabNav
// ---------------------------------------------------------------------------

function Breadcrumb({ title }: { title: string }) {
  return (
    <nav
      aria-label="Đường dẫn"
      style={{
        fontSize: 13,
        color: 'var(--asklaw-muted)',
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      <Link to="/" style={{ color: 'var(--asklaw-muted)', textDecoration: 'none' }}>
        Trang chủ
      </Link>
      <span aria-hidden>/</span>
      <Link to="/tim-kiem" style={{ color: 'var(--asklaw-muted)', textDecoration: 'none' }}>
        Tìm kiếm
      </Link>
      <span aria-hidden>/</span>
      <span
        style={{
          color: 'var(--asklaw-ink-soft)',
          maxWidth: 360,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
    </nav>
  );
}

function TabNav({ active, onChange }: { active: TabKey; onChange: (k: TabKey) => void }) {
  return (
    <div
      role="tablist"
      aria-label="Các phần văn bản"
      style={{
        position: 'sticky',
        top: 64,
        zIndex: 30,
        background: 'var(--asklaw-paper)',
        borderBottom: '1px solid var(--asklaw-border)',
        display: 'flex',
        gap: 4,
        // Horizontal scroll allowed for mobile tab overflow.
        // overflowY explicit hidden so the vertical scrollbar that
        // Safari/Chrome auto-render when content barely fits never shows.
        // (Founder UAT 2026-05-27: empty vertical scrollbar in tab nav.)
        overflowX: 'auto',
        overflowY: 'hidden',
        marginLeft: -8,
        marginRight: -8,
        padding: '0 8px',
      }}
    >
      {TABS.map((t) => {
        const isActive = t.key === active;
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={isActive}
            type="button"
            onClick={() => onChange(t.key)}
            className="corpus-tab"
            data-active={isActive || undefined}
            style={{
              position: 'relative',
              padding: '12px 16px',
              border: 0,
              background: 'transparent',
              color: isActive ? 'var(--asklaw-navy)' : 'var(--asklaw-muted)',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              fontWeight: isActive ? 600 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              minHeight: 44,
              transition: 'color 120ms ease',
            }}
          >
            {t.label}
            {isActive ? (
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 10,
                  right: 10,
                  bottom: -1,
                  height: 2,
                  background: 'var(--asklaw-navy)',
                  borderRadius: 2,
                }}
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1 - Tong quan
// ---------------------------------------------------------------------------

function TabTongQuan({ doc }: { doc: DocDetail }) {
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Tên văn bản', value: doc.title },
    {
      label: 'Số hiệu',
      value: doc.doc_num ? (
        <span style={{ fontFamily: 'var(--font-mono)' }}>{doc.doc_num}</span>
      ) : (
        <em style={{ color: 'var(--asklaw-muted)' }}>Chưa rõ</em>
      ),
    },
    {
      label: 'Loại văn bản',
      value: doc.doc_type_name ?? doc.doc_type_code ?? 'Chưa phân loại',
    },
    { label: 'Cơ quan ban hành', value: doc.agency_name ?? 'Chưa rõ' },
    { label: 'Ngày ban hành', value: formatDateVi(doc.issue_date) },
    { label: 'Ngày có hiệu lực', value: formatDateVi(doc.eff_from) },
    {
      label: 'Ngày hết hiệu lực',
      value: doc.eff_to
        ? formatDateVi(doc.eff_to)
        : doc.eff_status_code === 'CHL'
          ? 'Vẫn còn hiệu lực'
          : 'Không xác định',
    },
    {
      label: 'Tình trạng',
      value: <EffStatusBadge status={doc.eff_status_code} />,
    },
    {
      label: 'Phạm vi',
      value: doc.scope === 'TW' ? SCOPE_LABEL_VI.TW : SCOPE_LABEL_VI.DP,
    },
  ];

  return (
    <section aria-labelledby="tab-tong-quan-heading">
      <h2 id="tab-tong-quan-heading" style={{ ...sectionH2Style }}>
        Thông tin tổng quan
      </h2>
      <div className="corpus-overview-grid">
        <dl
          style={{
            margin: 0,
            padding: 20,
            background: 'var(--asklaw-paper-elev)',
            border: '1px solid var(--asklaw-border)',
            borderRadius: 12,
          }}
        >
          {rows.map((r) => (
            <div
              key={r.label}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 12,
                padding: '9px 0',
                borderBottom: '1px solid var(--asklaw-border)',
                fontSize: 14,
              }}
            >
              <dt style={{ color: 'var(--asklaw-muted)', fontWeight: 500 }}>{r.label}</dt>
              <dd style={{ margin: 0, color: 'var(--asklaw-ink)' }}>{r.value}</dd>
            </div>
          ))}
        </dl>
        <div>
          {doc.summary_text ? (
            <div
              style={{
                padding: 20,
                background: 'var(--asklaw-paper-elev)',
                border: '1px solid var(--asklaw-border)',
                borderLeft: '3px solid var(--asklaw-gold)',
                borderRadius: 12,
              }}
            >
              <h3 style={{ ...sectionH3Style }}>Tóm tắt</h3>
              <p
                style={{
                  margin: 0,
                  color: 'var(--asklaw-ink-soft)',
                  fontSize: 14.5,
                  lineHeight: 1.65,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'var(--font-sans)',
                }}
              >
                {doc.summary_text}
              </p>
              {doc.summary_source ? (
                <p
                  style={{
                    margin: '12px 0 0',
                    paddingTop: 10,
                    borderTop: '1px solid var(--asklaw-border)',
                    fontSize: 12,
                    color: 'var(--asklaw-muted)',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  [1] Nguồn tóm tắt: {doc.summary_source}
                </p>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                padding: 20,
                background: 'var(--asklaw-paper-sunk)',
                border: '1px dashed var(--asklaw-border)',
                borderRadius: 12,
                color: 'var(--asklaw-muted)',
                fontSize: 14,
              }}
            >
              Văn bản này chưa có bản tóm tắt. Vui lòng xem nội dung đầy đủ ở tab "Nội dung".
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 2 - Noi dung (RawHtmlViewer + TocSidebar)
// ---------------------------------------------------------------------------

function TabNoiDung({ doc }: { doc: DocDetail }) {
  // Sprint 5 SB5-01: if doc is PDF-sourced from vanban.chinhphu.vn,
  // render PdfViewer instead of HTML iframe. Avoid loading raw-html endpoint
  // (returns 404 - vanban_pdf docs have empty raw_html_r2_key).
  const isPdfSource = doc.source_tier === 'vanban_pdf' && !!doc.raw_pdf_url;

  const viewerRef = useRef<RawHtmlViewerHandle>(null);
  const [tocEntries, setTocEntries] = useState<TocEntry[]>([]);
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);

  const onTocReady = useCallback((entries: TocEntry[]) => {
    setTocEntries(entries);
  }, []);

  const onJump = useCallback((anchorId: string) => {
    setActiveAnchor(anchorId);
    viewerRef.current?.jumpToAnchor(anchorId);
  }, []);

  const hasToc = tocEntries.length > 0;

  if (isPdfSource) {
    return (
      <section aria-labelledby="tab-noi-dung-heading">
        <h2 id="tab-noi-dung-heading" style={{ ...sectionH2Style }}>
          Nội dung văn bản
        </h2>
        <PdfViewer
          docId={doc.doc_id}
          pdfUrl={doc.raw_pdf_url as string}
          title={doc.title}
          sourceExternalId={doc.source_external_id ?? null}
        />
      </section>
    );
  }

  return (
    <section aria-labelledby="tab-noi-dung-heading">
      <h2 id="tab-noi-dung-heading" style={{ ...sectionH2Style }}>
        Nội dung văn bản
      </h2>
      {!hasToc && <FallbackBanner />}
      <div
        className="corpus-content-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: hasToc ? 'minmax(220px, 280px) 1fr' : '1fr',
          gap: 24,
        }}
      >
        {hasToc ? (
          <TocSidebar
            entries={tocEntries}
            activeAnchor={activeAnchor}
            onJump={onJump}
          />
        ) : null}
        <article style={{ minWidth: 0 }}>
          <RawHtmlViewer ref={viewerRef} docId={doc.doc_id} onTocReady={onTocReady} />
        </article>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 3 - Van ban goc (outbound link to vbpl.vn)
// ---------------------------------------------------------------------------

function TabPdf({ doc }: { doc: DocDetail }) {
  // Sprint 5 SB5-01: outbound link source-aware.
  // vanban_pdf docs -> vanban.chinhphu.vn (source of truth for that tier).
  // vbpl_primary    -> vbpl.vn (current default).
  const isVanban = doc.source_tier === 'vanban_pdf';
  const externalId = doc.source_external_id ?? doc.doc_id;
  const externalUrl = isVanban
    ? `https://vanban.chinhphu.vn/?pageid=27160&docid=${encodeURIComponent(externalId)}&classid=1&orggroupid=1`
    : `https://vbpl.vn/TW/Pages/vbpq-toanvan.aspx?ItemID=${encodeURIComponent(doc.doc_id)}`;
  const externalLabel = isVanban
    ? 'Mở văn bản gốc tại vanban.chinhphu.vn'
    : 'Mở văn bản gốc tại vbpl.moj.gov.vn';
  const sourceLabel = isVanban
    ? `Nguồn: vanban.chinhphu.vn (Văn phòng Chính phủ) - docid: ${externalId}`
    : `Nguồn: vbpl.moj.gov.vn (Bộ Tư pháp) - ItemID: ${doc.doc_id}`;
  const sourceSentence = isVanban
    ? 'Bản gốc đầy đủ được lưu tại Cổng thông tin Văn bản Chính phủ. Bản tổng hợp tại vbpl.moj.gov.vn chưa công bố HTML đầy đủ ở thời điểm này.'
    : 'Bản gốc đầy đủ và các tệp đính kèm được lưu tại Cơ sở dữ liệu quốc gia về văn bản pháp luật.';

  return (
    <section aria-labelledby="tab-pdf-heading">
      <h2 id="tab-pdf-heading" style={{ ...sectionH2Style }}>
        Văn bản gốc
      </h2>
      <div
        style={{
          padding: 32,
          background: 'var(--asklaw-paper-elev)',
          border: '1px solid var(--asklaw-border)',
          borderRadius: 12,
          textAlign: 'center',
        }}
      >
        <p style={{ margin: '0 0 10px', fontWeight: 600, color: 'var(--asklaw-ink)' }}>
          {sourceSentence}
        </p>
        <p style={{ margin: '0 0 22px', color: 'var(--asklaw-ink-soft)', fontSize: 14 }}>
          Mở văn bản gốc của "{clip(doc.title, 80)}" trong tab mới để xem hoặc tải về.
        </p>
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '12px 24px',
            background: 'var(--asklaw-navy)',
            color: 'var(--asklaw-paper)',
            border: '1px solid var(--asklaw-navy-deep)',
            borderRadius: 8,
            textDecoration: 'none',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 15,
            minHeight: 48,
          }}
        >
          {externalLabel}
        </a>
        <p
          style={{
            marginTop: 18,
            fontSize: 12,
            color: 'var(--asklaw-muted)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          [1] {sourceLabel}
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 4 - Hieu luc timeline
// ---------------------------------------------------------------------------

function TabHieuLuc({ doc }: { doc: DocDetail }) {
  const [replacements, setReplacements] = useState<ReferenceEdge[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchReferences(doc.doc_id)
      .then((data) => {
        if (cancelled) return;
        const matches = data.references_in.filter(
          (r) => r.ref_type === 10 || r.ref_type === 1 || r.ref_type === 12,
        );
        setReplacements(matches);
      })
      .catch(() => {
        if (!cancelled) setReplacements([]);
      });
    return () => {
      cancelled = true;
    };
  }, [doc.doc_id]);

  const eff = doc.eff_status_code;
  const hhl = eff === 'HHL' || eff === 'HHL1P';

  const moc: Array<{ label: string; date: string | null; type: 'normal' | 'danger' }> = [
    { label: 'Ngày ban hành', date: doc.issue_date, type: 'normal' },
    { label: 'Ngày có hiệu lực', date: doc.eff_from, type: 'normal' },
    {
      label: 'Ngày hết hiệu lực',
      date: doc.eff_to,
      type: hhl ? 'danger' : 'normal',
    },
  ];

  return (
    <section aria-labelledby="tab-hieu-luc-heading">
      <h2 id="tab-hieu-luc-heading" style={{ ...sectionH2Style }}>
        Hiệu lực văn bản
      </h2>
      {hhl ? (
        <div
          role="alert"
          style={{
            display: 'flex',
            gap: 14,
            padding: 16,
            background: 'var(--asklaw-red-soft)',
            border: '1px solid var(--asklaw-red-soft)',
            borderLeft: '4px solid var(--asklaw-red)',
            borderRadius: 10,
            marginBottom: 20,
          }}
        >
          <span aria-hidden style={{ flexShrink: 0, marginTop: 2, color: 'var(--asklaw-red)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2L1 22h22L12 2z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M12 10v6M12 19h.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <div>
            <p
              style={{
                margin: '0 0 4px',
                fontWeight: 700,
                color: 'var(--asklaw-red-deep)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              Văn bản này đã {eff === 'HHL' ? 'hết hiệu lực' : 'hết hiệu lực một phần'}
              {doc.eff_to ? ` từ ngày ${formatDateVi(doc.eff_to)}` : ''}.
            </p>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--asklaw-red-deep)' }}>
              Khi tra cứu, hãy đối chiếu với văn bản hiện hành. Asklaw chỉ cung cấp thông tin tham
              khảo, không thay thế tư vấn pháp lý.
            </p>
            {replacements && replacements.length > 0 ? (
              <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 13.5 }}>
                {replacements.slice(0, 5).map((r, i) => (
                  <li key={`rep-${i}`} style={{ margin: '4px 0' }}>
                    {r.in_pool ? (
                      <Link
                        to={`/van-ban/${encodeURIComponent(r.source_doc_id)}`}
                        style={{ color: 'var(--asklaw-red-deep)', fontWeight: 600 }}
                      >
                        {r.title ?? r.source_doc_id}
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--asklaw-red-deep)' }}>
                        {r.title ?? 'Văn bản liên quan'}
                      </span>
                    )}{' '}
                    <span style={{ color: 'var(--asklaw-red-deep)', fontSize: 12 }}>
                      ({r.ref_label})
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      <ol
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          position: 'relative',
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 11,
            top: 6,
            bottom: 6,
            width: 2,
            background: 'var(--asklaw-border-strong)',
          }}
        />
        {moc.map((m, i) => (
          <li
            key={`moc-${i}`}
            style={{
              position: 'relative',
              paddingLeft: 36,
              paddingBottom: 18,
              fontSize: 14,
            }}
          >
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: 4,
                top: 2,
                width: 16,
                height: 16,
                transform: 'rotate(45deg)',
                background: m.type === 'danger' ? 'var(--asklaw-red)' : 'var(--asklaw-navy)',
                borderRadius: 2,
              }}
            />
            <div style={{ fontWeight: 600, color: 'var(--asklaw-ink)' }}>{m.label}</div>
            <div style={{ color: 'var(--asklaw-muted)', fontFamily: 'var(--font-mono)' }}>
              {m.date ? formatDateVi(m.date) : 'Chưa xác định'}
            </div>
          </li>
        ))}
      </ol>

      <p
        style={{
          marginTop: 18,
          padding: 12,
          background: 'var(--asklaw-paper-sunk)',
          borderRadius: 8,
          color: 'var(--asklaw-muted)',
          fontSize: 12.5,
          fontFamily: 'var(--font-mono)',
        }}
      >
        [1] Thông tin hiệu lực dựa trên dữ liệu vbpl.vn (Bộ Tư pháp). Vui lòng đối chiếu lại với
        nguồn chính thức khi cần.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tab 5 - Luoc do (reference graph)
// ---------------------------------------------------------------------------

function TabLuocDo({ doc }: { doc: DocDetail }) {
  // Sprint 5 SB5-01: vanban_pdf docs have no refs graph (no vbpl XML source).
  // Render explicit placeholder instead of calling references endpoint - avoids
  // a wasted Worker round-trip + UPL drift risk (no fabricated edges).
  const isPdfSource = doc.source_tier === 'vanban_pdf';
  const docId = doc.doc_id;

  const [inbound, setInbound] = useState<ReferenceEdge[] | null>(null);
  const [outbound, setOutbound] = useState<ReferenceEdge[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isPdfSource) return;
    let cancelled = false;
    fetchReferences(docId)
      .then((data) => {
        if (cancelled) return;
        setInbound(data.references_in);
        setOutbound(data.references_out);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [docId, isPdfSource]);

  if (isPdfSource) {
    return (
      <section aria-labelledby="tab-luoc-do-heading">
        <h2 id="tab-luoc-do-heading" style={{ ...sectionH2Style }}>
          Lược đồ văn bản
        </h2>
        <PdfRefsPlaceholder />
      </section>
    );
  }

  if (error) {
    return (
      <p style={{ color: 'var(--asklaw-red-deep)' }}>
        Không tải được lược đồ tham chiếu.
      </p>
    );
  }

  if (!inbound || !outbound) {
    return <DocSkeleton />;
  }

  return (
    <section aria-labelledby="tab-luoc-do-heading">
      <h2 id="tab-luoc-do-heading" style={{ ...sectionH2Style }}>
        Lược đồ văn bản
      </h2>
      <p style={{ color: 'var(--asklaw-muted)', fontSize: 13.5, marginTop: -4, marginBottom: 18 }}>
        Quan hệ giữa văn bản này và các văn bản khác trong hệ thống vbpl.vn.
      </p>

      <RefSection
        title="Văn bản tham chiếu đến văn bản này"
        emptyText="Chưa có văn bản nào tham chiếu đến văn bản này trong dữ liệu hiện tại."
        rows={inbound}
        getNavId={(r) => r.source_doc_id}
      />

      <div style={{ height: 24 }} />

      <RefSection
        title="Văn bản được tham chiếu"
        emptyText="Văn bản này chưa tham chiếu đến văn bản nào trong dữ liệu hiện tại."
        rows={outbound}
        getNavId={(r) => r.target_doc_id}
      />
    </section>
  );
}

function RefSection({
  title,
  emptyText,
  rows,
  getNavId,
}: {
  title: string;
  emptyText: string;
  rows: ReferenceEdge[];
  getNavId: (r: ReferenceEdge) => string;
}) {
  return (
    <div>
      <h3 style={{ ...sectionH3Style, marginTop: 0 }}>
        {title}{' '}
        <span style={{ fontWeight: 500, color: 'var(--asklaw-muted)', fontSize: 14 }}>
          ({rows.length})
        </span>
      </h3>
      {rows.length === 0 ? (
        <p
          style={{
            margin: 0,
            padding: 16,
            background: 'var(--asklaw-paper-sunk)',
            border: '1px dashed var(--asklaw-border)',
            borderRadius: 10,
            color: 'var(--asklaw-muted)',
            fontSize: 13.5,
          }}
        >
          {emptyText}
        </p>
      ) : (
        <div
          style={{
            border: '1px solid var(--asklaw-border)',
            borderRadius: 12,
            overflow: 'auto',
            background: 'var(--asklaw-paper-elev)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--asklaw-paper-sunk)' }}>
                <th style={thStyle}>Loại tham chiếu</th>
                <th style={thStyle}>Tên văn bản</th>
                <th style={thStyle}>Số hiệu</th>
                <th style={thStyle}>Ngày ban hành</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const navId = getNavId(r);
                const inPool = r.in_pool && Boolean(navId);
                return (
                  <tr
                    key={`${navId || 'ext'}-${i}`}
                    style={{
                      borderTop: '1px solid var(--asklaw-border)',
                      background: inPool ? 'transparent' : 'rgba(0, 0, 0, 0.015)',
                    }}
                    title={inPool ? undefined : 'Văn bản ngoài phạm vi dữ liệu hiện tại'}
                  >
                    <td style={tdStyle}>
                      <RefTypeBadge code={r.ref_type} />
                    </td>
                    <td style={tdStyle}>
                      {inPool ? (
                        <Link
                          to={`/van-ban/${encodeURIComponent(navId)}`}
                          style={{
                            color: 'var(--asklaw-ink)',
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                        >
                          {r.title ?? navId}
                        </Link>
                      ) : (
                        <span style={{ color: 'var(--asklaw-muted)' }}>
                          {r.title ?? 'Văn bản ngoài phạm vi'}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', fontSize: 13 }}>
                      {r.doc_num ?? '-'}
                    </td>
                    <td style={tdStyle}>{r.issue_date ? formatDateVi(r.issue_date) : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 14px',
  fontWeight: 600,
  color: 'var(--asklaw-muted)',
  fontSize: 12.5,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  color: 'var(--asklaw-ink)',
  verticalAlign: 'top',
};

// ---------------------------------------------------------------------------
// Tab 6 - Tai ve
// ---------------------------------------------------------------------------

function TabTaiVe({ doc }: { doc: DocDetail }) {
  const vbplUrl = `https://vbpl.vn/TW/Pages/vbpq-toanvan.aspx?ItemID=${encodeURIComponent(doc.doc_id)}`;
  return (
    <section aria-labelledby="tab-tai-ve-heading">
      <h2 id="tab-tai-ve-heading" style={{ ...sectionH2Style }}>
        Tải về văn bản
      </h2>
      <p style={{ marginTop: -4, color: 'var(--asklaw-muted)', fontSize: 13.5 }}>
        Tải bản gốc trực tiếp từ Cơ sở dữ liệu quốc gia về văn bản pháp luật (vbpl.vn).
      </p>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '16px 0 0',
          display: 'grid',
          gap: 10,
        }}
      >
        <li>
          <a
            href={vbplUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              padding: '14px 18px',
              background: 'var(--asklaw-paper-elev)',
              border: '1px solid var(--asklaw-border)',
              borderLeft: '3px solid var(--asklaw-navy)',
              borderRadius: 10,
              textDecoration: 'none',
              color: 'var(--asklaw-ink)',
              minHeight: 44,
            }}
          >
            <span style={{ color: 'var(--asklaw-navy)' }}>
              <DownloadIcon />
            </span>
            <span style={{ flex: 1 }}>
              <strong style={{ display: 'block', color: 'var(--asklaw-navy)' }}>
                Mở văn bản gốc tại vbpl.vn
              </strong>
              <span style={{ color: 'var(--asklaw-muted)', fontSize: 12.5 }}>
                Toàn văn HTML + tệp đính kèm. Mở trong tab mới.
              </span>
            </span>
          </a>
        </li>
      </ul>
      <p
        style={{
          marginTop: 14,
          fontSize: 12,
          color: 'var(--asklaw-muted)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        [1] Nguồn: vbpl.vn (Bộ Tư pháp). Asklaw không tự lưu trữ tệp gốc để đảm bảo bản dùng luôn
        cập nhật từ nguồn chính thức.
      </p>
    </section>
  );
}

function DownloadIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 4v12m0 0l-4-4m4 4l4-4M5 20h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Shared style + helpers
// ---------------------------------------------------------------------------

// Reading-width cho tab card/table (Tong quan, Hieu luc, Luoc do, Van ban goc,
// Tai ve). Hep hon main 1180px de table Luoc do khong dac full-width nhin lech;
// rong hon header text-block 760px de bang 4 cot du cho doc. Tab Noi dung opt-out.
const CONTENT_MAX_W = 960;

const sectionH2Style: React.CSSProperties = {
  margin: '0 0 16px',
  fontSize: 21,
  fontWeight: 600,
  color: 'var(--asklaw-navy)',
  fontFamily: 'var(--font-display)',
  letterSpacing: '-0.005em',
};

const sectionH3Style: React.CSSProperties = {
  margin: '0 0 10px',
  fontSize: 15.5,
  fontWeight: 600,
  color: 'var(--asklaw-navy)',
  fontFamily: 'var(--font-sans)',
};

function formatDateVi(iso: string | null | undefined): string {
  if (!iso) return 'Chưa rõ';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function clip(s: string, max: number): string {
  if (!s) return '';
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + '...';
}

function DocSkeleton() {
  return (
    <div
      aria-busy
      aria-label="Đang tải văn bản"
      style={{ display: 'grid', gap: 14, padding: 20 }}
    >
      <ShimmerLine width="60%" height={28} />
      <ShimmerLine width="40%" height={14} />
      <div style={{ height: 8 }} />
      <ShimmerLine width="100%" height={14} />
      <ShimmerLine width="95%" height={14} />
      <ShimmerLine width="98%" height={14} />
      <ShimmerLine width="80%" height={14} />
    </div>
  );
}

function ShimmerLine({ width, height }: { width: string; height: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'block',
        width,
        height,
        borderRadius: 6,
        background:
          'linear-gradient(90deg, var(--asklaw-paper-sunk) 0%, var(--asklaw-border) 50%, var(--asklaw-paper-sunk) 100%)',
        backgroundSize: '200% 100%',
        animation: 'corpus-shimmer 1.4s ease-in-out infinite',
      }}
    />
  );
}
